import { motion } from 'framer-motion';
import { CheckCircle, ClipboardList, QrCode, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { trackCTA } from '../utils/analytics';

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'IDGate — Hotel Aadhaar Check-in',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR', description: 'Free pilot' },
  description:
    'Replace paper guest registers with Aadhaar-verified digital check-in for hotels. UIDAI-backed, DPDP compliant, Form C ready.',
  provider: { '@type': 'Organization', name: 'IDGate', url: 'https://idgate.in' },
};

const faqData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Is IDGate compliant with hotel Form C requirements in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. IDGate generates a verified digital guest record equivalent to Form C, backed by UIDAI Aadhaar authentication. The record includes name, address, ID number, and a timestamped consent trail — satisfying police and fire inspection requirements.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does the guest need to install any app?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Guests need the UIDAI Aadhaar app (mAadhaar) installed on their phone to complete verification. The check-in itself takes under 60 seconds.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does IDGate integrate with my hotel PMS?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'IDGate delivers verified guest records to a Google Sheet or via webhook. Most hotel PMS systems (Hotelogix, eZee, Stayflexi) can receive webhook data or import from Sheets.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is guest data stored by IDGate?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. IDGate is DPDP compliant and does not retain guest Aadhaar data. The verification is handled directly by UIDAI. Your hotel receives only the consent-backed record you need.',
      },
    },
  ],
};

const STEPS = [
  { icon: QrCode, title: 'Guest scans QR at front desk', body: 'One QR code mounted at your reception. No app download required for staff.' },
  { icon: ShieldCheck, title: 'Aadhaar verified by UIDAI', body: 'Identity confirmed in under 60 seconds directly by UIDAI. No photocopy. No manual entry.' },
  { icon: ClipboardList, title: 'Record lands in your system', body: 'Verified guest record sent to your Google Sheet or PMS via webhook. Audit-ready instantly.' },
];

const FAQS = faqData.mainEntity;

const Hotels = () => (
  <>
    <SEOHead
      title="Aadhaar Check-in for Hotels — Replace Paper Guest Registers"
      description="Replace Form C paper registers with Aadhaar-verified digital check-in. UIDAI-backed, DPDP compliant, audit-ready. Free pilot for independent hotels in India."
      keywords="hotel guest register India, Form C compliance software, hotel Aadhaar check-in, digital guest register hotel India, UIDAI hotel verification, hotel check-in software India, replace paper guest register hotel"
      url="https://idgate.in/hotels"
      structuredData={[structuredData, faqData]}
    />

    {/* Hero */}
    <section className="border-b border-slate-200 bg-white">
      <div className="container-custom py-20 lg:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <p className="eyebrow">For Hotels & Resorts</p>
          <h1 className="section-title mt-3">
            Your paper guest register<br className="hidden sm:block" /> is a compliance risk.
          </h1>
          <p className="mt-6 text-lg text-slate-600">
            Replace Form C paper stacks and photocopied ID cards with Aadhaar-verified digital check-in.
            Every guest record is verified by UIDAI, timestamped, and exportable in 30 seconds.
          </p>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link
              to="/contact"
              className="btn-primary"
              onClick={() => trackCTA('start_pilot_hotels', 'hotels_hero')}
            >
              Start free pilot
            </Link>
            <Link
              to="/demo"
              className="btn-secondary"
              onClick={() => trackCTA('view_demo_hotels', 'hotels_hero')}
            >
              See how it works
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* Pain points */}
    <section className="bg-slate-50 py-16">
      <div className="container-custom">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="section-title">What a surprise inspection reveals</h2>
          <p className="mt-4 text-slate-600">Most independent hotels fail on at least two of these.</p>
        </div>
        <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            'Illegible handwriting in the guest register',
            'Guests registered as "N/A" or fake names',
            'No way to export records for a date range',
            'Photocopied IDs that are blurry or expired',
            'No consent trail for data collected',
            'Paper registers going back years with no backups',
          ].map(pain => (
            <div key={pain} className="rounded-xl border border-red-100 bg-white p-5 shadow-sm">
              <p className="text-sm font-medium text-slate-700">✗ &nbsp;{pain}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* How it works */}
    <section className="bg-white py-16">
      <div className="container-custom">
        <h2 className="section-title text-center">How IDGate works at your front desk</h2>
        <div className="mx-auto mt-12 grid max-w-4xl gap-8 sm:grid-cols-3">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
            >
              <step.icon className="h-8 w-8 text-primary-600" aria-hidden />
              <h3 className="mt-4 font-semibold text-slate-900">{step.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{step.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Benefits */}
    <section className="bg-primary-700 py-16 text-white">
      <div className="container-custom">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold tracking-tight">What you get with IDGate</h2>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2">
            {[
              'Aadhaar-verified identity for every guest',
              'Form C equivalent — satisfies police & fire inspections',
              'Records exportable in under 30 seconds',
              'DPDP compliant — you own the data, IDGate is the pipe',
              'Google Sheet or PMS integration via webhook',
              'No new dashboard — works with what you have',
              'Staff training: 15 minutes, one screen',
              'Free 30-day pilot — no contract, no card',
            ].map(benefit => (
              <li key={benefit} className="flex items-start gap-3">
                <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent-400" aria-hidden />
                <span className="text-sm text-primary-100">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="bg-white py-16">
      <div className="container-custom">
        <h2 className="section-title text-center">Common questions from hotel owners</h2>
        <div className="mx-auto mt-10 max-w-2xl divide-y divide-slate-200">
          {FAQS.map(faq => (
            <details key={faq.name} className="group py-5">
              <summary className="flex cursor-pointer items-center justify-between font-medium text-slate-900 marker:content-none">
                {faq.name}
                <span className="ml-4 flex-shrink-0 text-slate-400 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{faq.acceptedAnswer.text}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="border-t border-slate-200 bg-slate-50 py-16">
      <div className="container-custom text-center">
        <h2 className="section-title">Ready to pass your next inspection?</h2>
        <p className="mt-4 text-slate-600">Free 30-day pilot. One afternoon to set up. No contract.</p>
        <Link
          to="/contact"
          className="btn-primary mt-8 inline-block"
          onClick={() => trackCTA('start_pilot_hotels', 'hotels_footer_cta')}
        >
          Start free pilot for your hotel
        </Link>
      </div>
    </section>
  </>
);

export default Hotels;
